import * as PIXI from 'pixi.js'
import { TweenMax, TimelineMax, Power0, Power1, Power2, Power3, Power4, Back, Bounce, Elastic, Expo, Sine } from 'gsap'
import PixiPlugin from 'gsap/src/PixiPlugin'
import { trace } from 'rytm-helpers'

import shapesMap from './../lib/shapes_map'
import VisualObject from './VisualObject'
import { rand, scaleToPx, absPositionX, absPositionY, relPositionX, relPositionY } from '../lib/math_helpers'
import TextObject from './TextObject'



const TPF = 1 // ticks per frame (FPS)
const OFFSET_Y_F = .1 // Y offset f (if 0 all will be centered)
const OFFSET_Y_MOVE = -.05 // Y offset (move)
const BAR_WIDTH = .4 // relative to window width
const MAX_BAR_AGE = 10 // after X bars all entities are destroyed
const MOVE_F_LABEL = .1 // label layer speed F
const MOVE_F_FRONT = .05 // front layer speed F
const MOVE_F_SPACER = .04 // spaces layer speed F
const MOVE_F_VARIOUS = .03 // various layer speed F
const MOVE_F_BACK = .02  // bg layer speed F
const MIN_LETTER_DIST = .17 // minimal letter distance (relative to window width)

class VisualStage {
  /**
  * VisualObject class constructor  
  * @constructor
  **/
  constructor(params) {
    this.events = {
      onNextFrame: params.onNextFrame,
      onNextBar: params.onNextBar
    }
    this._stagedIndex = 0
    this._staged = [] // VOs added to stage
    this._tickerCC = 0
    this._frame = 0
    this._bar = 0
    this._barX = -BAR_WIDTH
    this._pointer = {x: 0, y: 0}
    this._lastLetterPos = {x: -10, offsetY: -10}
    this._lastBgId = false
    this.mouseP = {x: window.innerWidth * .5,y: window.innerHeight * .5}
  }
  /**
   * INIT
   * @param {Node} wrapper 
   */
  init(wrapper) {
    trace("VisualStage init")
    this.wrapper = wrapper
    const params = {
      width: wrapper.offsetWidth,
      height: wrapper.offsetHeight,
      transparent: true, 
      preserveDrawingBuffer: true,
      resolution: 1,
      antialias: true
    }
    this.app = new PIXI.Application(params)
    this.wrapper.appendChild(this.app.view)
    // containers
    this.mainContainer = new PIXI.Container()
    this.app.stage.addChild(this.mainContainer)
    // bg container
    this.bgContainer = new PIXI.Container()
    this.mainContainer.addChild(this.bgContainer)
    // various container
    this.variousContainer = new PIXI.Container()
    this.mainContainer.addChild(this.variousContainer)
    // spacer container
    this.spacerContainer = new PIXI.Container()
    this.mainContainer.addChild(this.spacerContainer)
    // front container
    this.frontContainer = new PIXI.Container()
    this.mainContainer.addChild(this.frontContainer)
    // label container
    this.labelContainer = new PIXI.Container()
    this.mainContainer.addChild(this.labelContainer)
    // ticker
    this.app.ticker.add(this.onTick.bind(this))
  }
  /**
   * DESTROY
   */
  destroy() {
    if (this.app) {
      this.wrapper.removeChild(this.app.view)
      this.app.stage.destroy()
      this.app.destroy()
      this.app = false
      trace("VisualStage was destroyed")
    }
  }

  onWindowResize() {
    clearTimeout(this.resizeTimeout)
    this.resizeTimeout = setTimeout(() => {
      const parent = this.app.view.parentNode
      this.app.renderer.resize(parent.clientWidth, parent.clientHeight)
      // each object resize
      this._staged.forEach((obj) => {
        obj.onWindowResize()
      })
    }, 200)
  }

  onMouseDown(mouseP) {
  }
  onMouseUp(mouseP) {
  }
  onMouseMove(mouseP) {
    this.mouseP = mouseP
  }

  onTick() {
    if (this._tickerCC == 0) {
      this.nextFrame()
    }
    this._tickerCC++
    this._tickerCC = this._tickerCC > TPF ? 0 : this._tickerCC
  }
  nextFrame() {
    this._frame++
    // fire event
    if (this.events.onNextFrame) {
      this.events.onNextFrame(this._frame)
    }
    // check BAR
    if (this._lastBarDistance() >= BAR_WIDTH) {
      this.nextBar()
    }
    // move Bar
    if (this._bar > 1/BAR_WIDTH) {
      this._moveContainersX()
    }
    this._moveContainersY()
  }
  nextBar() {
    this._bar++
    if (this.events.onNextBar) {
      this.events.onNextBar(this._bar)
    }
    // trace("~ BAR", this._bar, " staged:", this._staged.length, "front:", this.frontContainer.children.length, "back:", this.bgContainer.children.length)
    this._killOutdated()
    this._barX += BAR_WIDTH
    // set pointer
    this._pointer.x = this._barX
    this._drawBarLabel()
    this._drawBackground()
  }

  /**
   * On next entity
   * @param {SoundEvent} sndEvent
   */
  nextEntity(sndEvent) {
    if (!this._drawLetter()) {
      // spacer 1 vs spacer 2
      if (this._randomize(.5)) {
        this._drawSpacer()
      } else {
        this._drawSpacer2()
      }
    }
  }
  /**
   * Special Entity (sound)
   */
  nextVarious(sndEvent) {
    // various 1 vs various 2
    if (this._randomize(.5)) {
      this._drawVarious()
    } else {
      this._drawVarious2()
    }
    
  }
  /**
   * Pauza
   */
  nextPause(time) {
    this._drawPause()
  }
  /**
   * Next mouse path event
   */
  nextMousePath() {
    this._drawMousePath()
  }


  /**
   * ### PRIVATE METHODS ###
   */
  _randomize(f) {
    const max = 1000
    const pom = rand(1,max)
    return pom < max * f
  }
  /**
   * Move front and back containers
   */
  _moveContainersX() {
    const wW = window.innerWidth
    const newX = -(this._pointer.x * wW) + wW * .8
    const moveSpeedF = this._getMoveXSpeedF()
    
    // Containers (all move X)
    const distLabelX = this.labelContainer.x - newX
    this.labelContainer.x -= distLabelX * moveSpeedF * MOVE_F_LABEL
    const distFrontX = this.frontContainer.x - newX
    this.frontContainer.x -= distFrontX * moveSpeedF * MOVE_F_FRONT
    const distSpacerX = this.spacerContainer.x - newX
    this.spacerContainer.x -= distSpacerX * moveSpeedF * MOVE_F_SPACER
    const distVariousX = this.variousContainer.x - newX
    this.variousContainer.x -= distVariousX * moveSpeedF * MOVE_F_VARIOUS
    const distBgX = this.bgContainer.x - newX
    this.bgContainer.x -= distBgX * moveSpeedF * MOVE_F_BACK
  }
  /**
   * Move front and back containers
   */
  _moveContainersY() {
    const wH = window.innerHeight
    const mY = wH * .5 - this.mouseP.y
    const newY = mY * .05
    // Containers (only selected move Y)
    const distFrontY = this.frontContainer.y - newY
    this.frontContainer.y -= distFrontY * MOVE_F_FRONT
    const distSpacerY = this.spacerContainer.y + newY
    this.spacerContainer.y -= distSpacerY * MOVE_F_SPACER
    const distVariousY = this.variousContainer.y - newY
    this.variousContainer.y -= distVariousY * MOVE_F_VARIOUS
  }
  

  _lastBarDistance() {
    return this._pointer.x - this._barX
  }

  _drawSpacer() {
    const x = this._pointer.x
    const id = this._getRandomShapeId("spaces")
    const yMax = 50 * OFFSET_Y_F
    const offsetY = (rand(1, yMax) - yMax * .5) * .01
    const scale = rand(25,28) * .01
    this._drawObject(id, x, offsetY, scale, (vo) => {
      this._addToStage(vo, this.spacerContainer)
      this._pointer.x += vo.getWidth()
    })
  }
  // Zygzaki (space_1, space_2, space_3)
  _drawSpacer2() {
    const x = this._pointer.x
    const id = this._getRandomShapeId("spaces2")
    const yMax = 50 * OFFSET_Y_F
    const offsetY = (rand(1, yMax) - yMax * .5) * .01
    const scale = rand(15,18) * .01
    this._drawObject(id, x, offsetY, scale, (vo) => {
      this._addToStage(vo, this.spacerContainer)
      this._pointer.x += vo.getWidth() * 2
    })
  }
  _drawVarious() {
    const x = this._pointer.x
    const id = this._getRandomShapeId("various")
    const yMax = 50 * OFFSET_Y_F
    const offsetY = (rand(1, yMax) - yMax * .5) * .01
    const scale = rand(14,18) * .01
    this._drawObject(id, x, offsetY, scale, (vo) => {
      this._addToStage(vo, this.variousContainer)
      // trace("various width: ", vo.getWidth(), "with scale:", vo.scale)
      this._pointer.x += vo.getWidth() * 2
    })
  }
  // chord & stripes
  _drawVarious2() {
    const x = this._pointer.x
    const id = this._getRandomShapeId("various2")
    const yMax = 50
    const offsetY = (rand(1, yMax) - yMax * .5) * .01
    const scale = rand(7,9) * .01
    this._drawObject(id, x, offsetY, scale, (vo) => {
      this._addToStage(vo, this.variousContainer)
      // trace("various width: ", vo.getWidth(), "with scale:", vo.scale)
      this._pointer.x += vo.getWidth() * 2
    })
  }
  _drawMousePath() {
    // const id = this._getRandomShapeId("mouse")
    // const relativeMouseX = relPositionX(this.mouseP.x)
    // const contenerX = relPositionX(this.labelContainer.x)
    // const x = relativeMouseX - contenerX
    // const y = relPositionY(this.mouseP.y - 62)
    // const offsetY = y - 0.5
    // const scale =  .01 - relativeMouseX * .01
    // this._drawObject(id, x, offsetY, scale, (vo) => {
    //   this._addToStage(vo, this.labelContainer)
    // })
  }

  /**
   * draw Letter
   */
  _drawLetter() {
    const x = this._pointer.x
    if (x - this._lastLetterPos.x < MIN_LETTER_DIST) {
      // trace("can't draw, letter to close")
      return false
    }
    const id = this._getRandomShapeId("letters")
    const yMax = 30
    const offsetY = (rand(1, yMax) - yMax * .5) * .01
    const scale = rand(4,18) * .01
    const marginX = rand(5, 10) * .01
    this._lastLetterPos.x = x
    this._lastLetterPos.offsetY = offsetY
    this._drawObject(id, x, offsetY, scale, (vo) => {
      this._addToStage(vo, this.frontContainer)
      this._pointer.x += vo.getWidth() + marginX
    })
    return true
  }
  /**
   * draw Pause
   */
  _drawPause() {
    const id = this._getRandomShapeId("pauses")
    const x = this._pointer.x
    const yMax = 50
    const offsetY = (rand(1, yMax) - yMax * .6) * .01
    const scale = .04
    this._drawObject(id, x, offsetY, scale, (vo) => {
      this._addToStage(vo, this.frontContainer)
      this._pointer.x += vo.getWidth() + rand(2,10) * .01
    })
  }
  /**
   * draw Background
   */
  _drawBackground() {
    let id = this._getRandomShapeId("backgrounds")
    while( id == this._lastBgId) {
      id = this._getRandomShapeId("backgrounds")  
    }
    const isHoriz = this._isHorizontalStage()
    this._lastBgId = id
    const x = this._pointer.x + rand(1,5) * .01
    const offsetY = isHoriz ? 0 : .2
    const scale = rand(30,35) * .01
    this._drawObject(id, x, offsetY, scale, (vo) => {
      this._addToStage(vo, this.bgContainer)
    })
  }

  /**
   * Draw object
   * @param {String} id 
   * @param {Number} x 
   * @param {Number} offsetY 
   * @param {Number} scale 
   * @param {Function} callback 
   */
  _drawObject(id, x, offsetY, scale, callback = null) {
    const params = {
      id,
      bar: this._bar,
      onReady: () => {
        vo.setScale(scale)
        vo.setPosition({ x })
        vo.verticalAlign(offsetY + OFFSET_Y_MOVE)
        if (callback) {
          callback(vo)
        }
      }
    }
    let vo = new VisualObject(params)
    vo.buildSprite()
  }

  /**
   * Draw Text - bar label
   */
  _drawBarLabel() {
    const params = {
      txt: this._bar,
      bar: this._bar
    }
    const y = this._isHorizontalStage() ? .1 : .02
    let to = new TextObject(params)
    to.buildText()
    to.setPosition({x: this._pointer.x + .2, y})
    this._addToStage(to, this.labelContainer)
  }


  /**
   * Add to stage
   * @param {VisualObject} vo 
   */
  _addToStage(vo, container) {
    this._stagedIndex++
    vo.uid = this._stagedIndex
    this._staged.push(vo)
    vo.addToStage(container)
  }
  /**
   * Remove from
   * @param {VisualObject} vo 
   */
  _removeFromStage(vo) {
    vo.destroy()
    let tmp = []
    this._staged.forEach((obj) => {
      if (obj.uid !== vo.uid) {
        tmp.push(obj)
      }
    })
    this._staged = tmp
  }
  /**
   * remove old cells (outside of window bounds)
   */
  _killOutdated() {
    const maxBar = this._bar - MAX_BAR_AGE
    this._staged.forEach((obj) => {
      if (obj.bar < maxBar) {
        this._removeFromStage(obj)
      }
    })
  }

  _getRandomShapeId(group) {
    let id = false
    const list = shapesMap[group]
    if (list) {
      id = list[Math.floor(Math.random() * list.length)];
    } else {
      console.warn("Unknown shape group", group)
    }
    return id
  }
  
  /**
   * Uzależnienie prędkości poruszania kontenera od pozycji myszy
   */
  _getMoveXSpeedF() {
    const mY = relPositionY(this.mouseP.y)
    const f = 1
    const speedF = 1 + mY * f - f * .5
    return speedF
  }
  /**
   * is horizontal (eg Mobile) view
   */
  _isHorizontalStage() {
    return window.innerHeight >= window.innerWidth
  }

}
export default VisualStage